// owner-map: OP-01.spa
// Forecast-submit command (EPIC #157, phase P5.1 — design §2.2).
//
// `submitForecast` / `doForecast` / `postSubmit` moved VERBATIM out of
// `hooks/useForecastSubmit.js`. The only behavioral rewrites are the four the
// migration mandates:
//   • `runStateRef.current`   → `deps.store.getState()`   (retires §7 #1; the live read
//                                is now first-class, so this path is finally unit-testable)
//   • `execByRunIdRef.current`→ the `execTable` module singleton (§7 #3)
//   • `draftAdoptionRef.current` → the module-level `draftAdoption` (§7 #10 — submit and
//                                doForecast live in one file now, so the hand-off is plain
//                                module state, no shared ref)
//   • `postSubmit(nfc, q)`    → `postSubmit(deps, nfc, q)` / `doForecast(...)` →
//                                `doForecast(deps, {...})` (deps threaded, not closed over)
// Everything else — the §7 #2 abort arm-order, the shared compare `ac.signal`, the exec
// claim/release-in-`finally`, the "user navigated away" gates, the deliberately-incomplete
// reasoning — is byte-preserved. Do NOT "tidy" any of it.
//
// I/O goes through `deps.services.forecastClient.genForecastFromServer` (design §2.2), the
// seam Lane A's `makeFakeForecastService` fakes. `contractFromPreprocessed` (pure) and
// `callLlm` stay direct imports; the preprocess/health calls go through `deps.services.api`
// (P5.11 drained the raw fetches — the submit-abort signal is threaded into preprocess).
//
// SCOPE NOTE (P6/P7): the `ui`/`refs`/`callbacks`/`tour` bags below are the honest shape of
// a verbatim command extraction — submit is entangled with React UI state. They shrink when
// P6 folds boot/effects into `bootForecasts` (retiring several refs) and P7 pushes ephemeral
// UI state down into components. Prune this deps surface there; don't re-grow it here.
import { applyNarrationFrame } from "../lib/forecast/narrationRouter.js";
import { applyUpdateFrame } from "../lib/forecast/updateRouter.js";
import { liveEventsReconcileInPlace, projectLiveEvents } from "../lib/forecast/liveTriggers.js";
import { recordUiEvent } from "../lib/logRingBuffer";
import { legacyText, recordLegacyEvent, recordTextEvent } from "../lib/telemetry/groups.js";
import { newMessageId } from "../lib/chatTurn.js";
import { recordForecastLanded, claimNudge } from "../lib/forecast/customTriggerNudge.js";
import { CUSTOM_TRIGGER_REMINDER } from "../onboarding/sayerTour.js";
import {
  recordPreprocessRequest,
  recordPreprocessResult,
  recordContractKey,
} from "../lib/contractTelemetry";
import { persistMessage } from "../lib/messages";
import {
  runCreated,
  contractSet,
  traceAllocated,
  runCompleted,
  runErrored,
  runRemoved,
  runStarted,
  activeSet,
  comparisonSet,
  comparisonCleared,
  entryAppended,
  draftAdopted,
  runProgress,
  contractKeySet,
} from "../lib/forecast/reducer.js";
import {
  progressPayload,
  makeProgressHandler,
  startProgressIntro,
} from "../lib/forecast/progress.js";
import { makeRun, makeEntry, STATUS } from "../lib/forecast/model.js";
import { assessmentText, hasForecastSummaryMessage } from "../lib/forecast/summaryPost.js";
import { effectiveTriggerModel } from "../lib/forecast/engines.js";
import { DRAFT_CONV_ID } from "../lib/forecast/draft.js";
import { captureChatTarget, chatTargetForRun } from "../lib/forecast/chatTarget.js";
import { flushHeld } from "../store/heldMessages.js";
import type { ChatTarget } from "../lib/forecast/chatTarget.js";
import { POST_TOUR_EXAMPLES_KEY } from "../lib/forecast/storageKeys.js";
import { entryToMessageBody } from "../lib/forecast/hydrate.js";
import { contractFromPreprocessed, exactDeadlineOf } from "../lib/forecast/forecastClient.js";
import { resolutionOptionsOf } from "../lib/forecast/resolutionChoice.js";
import { routePreprocess, editBufferOf, extractMultiway } from "../lib/forecast/multiway.js";
import { addComparePendingLeg, removeComparePendingPair } from "../lib/comparePending";
import { hasTimeframeHint, looksLikeMultiForecastIntent } from "../lib/questionIntent.js";
import { callLlm } from "../lib/forecast/llmClient.js";
import { parseResolveDateLocal } from "../lib/formatDates.js";
import { FORECASTER_INFO, tagOwner, effectiveTier } from "../lib/forecast/engines.js";
import { shouldClearSelectionOnSubmit } from "../lib/series/restoreSeries.js";
import { execTable } from "../store/execTable.js";
import { bindIntent } from "../lib/telemetry/intentDeps.js";
import { intentEmitter } from "../lib/telemetry/intentEmitter.js";
import {
  beginIntent,
  type BeginIntentOptions,
  type IntentHandle,
} from "../lib/telemetry/instrument.js";
import type { CommandDeps } from "./deps.js";
import {
  FORECAST_RECOVERY_WINDOW_MS,
  isRecoveryWaitExpiredError,
  isUnansweredRecoveryError,
} from "../lib/forecastStream.js";

// ── deps typing ─────────────────────────────────────────────────────────────
// Pragmatic types at the React boundary (decision C): the store/services/args carry
// real domain types; the UI setters / imperative refs / callbacks are typed loosely
// (they're React handles this command doesn't own). Tighten as P6/P7 move them.

type AnyFn = (...args: any[]) => any;
type Ref<T = any> = { current: T };

/** The paced opening-steps intro controller returned by startProgressIntro. */
interface IntroPacer {
  active: () => boolean;
  defer: (apply: () => void) => boolean;
  stop: () => void;
}

/** React state values + setters submit reads/writes (ephemeral UI state, moves down in P7). */
interface SubmitUi {
  input: string;
  loading: boolean;
  forecastEngine: string;
  compareMode: boolean;
  compareEngine: string | null;
  triggerLlm: any;
  compareTriggerLlm: any;
  debugMode: boolean;
  series: Record<string, any>;
  // Whether multi-value ("multiway") mode is enabled in this deployment
  // (chatboard-multiway flag). Gates whether a leading `MW:` prefix routes the
  // question through the multi-value system; false → `MW:` has no special meaning.
  multiwayEnabled?: boolean;
  // Whether automatic question-type routing is enabled in this deployment
  // (chatboard-auto-question-type flag). When true, an unprefixed prompt is
  // preprocessed via /preprocess/auto (the contract server classifies binary vs
  // categorical vs numeric); false → unprefixed prompts take the plain binary path,
  // i.e. today's `MW:`-only behaviour.
  autoQuestionTypeEnabled?: boolean;
  // A registered preprocess-variant id from the DEBUG picker, or "" for none
  // (hooks/usePreprocessVariant.js). Forwarded on the AUTO preprocess call only — a
  // variant is an auto authoring strategy, so the binary/multi endpoints have nothing to
  // dispatch on — and honoured only in a deployment whose debug surface is available; the
  // chatboard server re-decides per request and drops it otherwise. Never a prompt: the
  // contract server looks the strategy up in its own reviewed registry by this id.
  preprocessVariantId?: string;
  // Whether typed IDL updates fold into the pending forecast lane.
  forecastLiveProgressEnabled?: boolean;
  setInput: AnyFn;
  setLoading: AnyFn;
  setForecastingPhase: AnyFn;
  setPendingPreprocess: AnyFn;
  setActiveTrace: AnyFn;
  setLastObs: AnyFn;
  setForecastEngine: AnyFn;
}

/** Stable imperative handles (§7 #2 abort refs among them; several retire in P6). */
interface SubmitRefs {
  forecastAbortRef: Ref;
  submitAbortRef: Ref;
  slideCache: Ref;
  pushSayerMsgRef: Ref;
  preprocessConfirmedRef: Ref;
  contractNotePendingRef: Ref;
  lastTourPendingIdRef: Ref;
  postTourNudgeTimerRef: Ref;
  postTourTipRef: Ref;
  postTourTipRunRef: Ref;
  pendingEngineRevertRef: Ref;
  // Consecutive forecast-error streak (App-owned useRef(0)): incremented on each
  // non-abort forecast failure, reset to 0 on a successful completion. Drives the
  // Sayer console retry nudge — "try again" on the first error, "submit a bug" once
  // it errors a second (or later) time in a row.
  forecastErrorStreakRef: Ref;
  /** Current fail-closed live-progress gate; unlike `ui`, this can change mid-stream. */
  forecastLiveProgressEnabledRef?: Ref<boolean>;
}

/** App-provided callbacks submit orchestrates (zoom/animation/series/ToS/draft). */
interface SubmitCallbacks {
  autoZoom: AnyFn;
  blinkContract: AnyFn;
  startForecastAnim: AnyFn;
  fetchLiveSeries: AnyFn;
  /** Empty the plotted signal selection and release its per-forecast ownership. */
  clearSeriesSelection: AnyFn;
  guardForTos: AnyFn;
  clearDraft: AnyFn;
  /**
   * The one reattach entry point (`useBootForecasts`' `reattachToForecast`): hands a run whose
   * live recovery ended without an answer back to the resume path. Optional — absent, the run
   * is simply left `running` for a log click, a reload, or the boot sweep to pick up.
   */
  reattachRunning?: AnyFn;
}

/** Transient (never persisted) line for a still-running run this tab will not reattach itself. */
export const STILL_RUNNING_RELOAD_MESSAGE =
  "Still running — the connection to this forecast dropped. Reload to reconnect.";

/** Live getters for App.jsx's tour module globals (threaded, never copied — §7 #8). */
interface SubmitTour {
  getTourFixture: AnyFn;
  getTourBranchIncrease: AnyFn;
}

/** Full deps for the submit command: the shared base + the React-bound bags. */
export interface SubmitDeps extends CommandDeps {
  ui: SubmitUi;
  refs: SubmitRefs;
  callbacks: SubmitCallbacks;
  tour: SubmitTour;
}

/** Args for the public submit op — mirrors the old `submit(overrideText, overrideEngine, force)`. */
export interface SubmitArgs {
  /** The control that started it (telemetry `origin`), when the caller knows it. */
  origin?: BeginIntentOptions["origin"];
  overrideText?: string;
  overrideEngine?: string;
  force?: boolean;
  /** The chat turn this submit belongs to (lib/chatTurn.js), passed by the caller that
   *  opened it — null/absent for a non-chat submit. Timing, plus its `msgId`. */
  turn?: ChatTurnLike | null;
}

/** The part of lib/chatTurn.js's ChatTurn a submit uses. */
interface ChatTurnLike {
  id: string | null;
  msgId?: string | null;
  msgText?: string | null;
  /** Set when the message was an example question the user clicked. */
  fromExample?: boolean;
  /** The control the message was sent from (`chat.send`, `ask-hero.submit`, …). */
  origin?: string | null;
  /** Where the turn's lines are filed, captured at send. */
  target?: ChatTarget | null;
  phase(name: string): () => void;
  mark(name: string): void;
  finish(outcome: string): void;
}

/** Args for the post-preprocess core — mirrors `doForecast(q, engine, preprocessed, _, pendingId)`. */
export interface DoForecastArgs {
  q: string;
  engine: string;
  preprocessed: any;
  // Nullable to match the body's contract: the no-pendingId path is real (the compare
  // branch falls back to a synthesized `fc-<ts>` id, and every store write is `if (pendingId)`-
  // guarded). Typing it honestly keeps future callers off `as any`.
  pendingId: string | null;
  /** The user message's `msg_id`, stamped on the persisted question (lib/chatTurn.js). */
  msgId?: string | null;
  /** The preprocess panel's `routing`, kept on the reload handle for a later SUBMIT EDIT. */
  routing?: object | null;
}

// ── module state (§7 #10) ────────────────────────────────────────────────────
// Draft entries pending persistence under a real forecastId. Populated when a run is
// created and the draft is adopted onto its conversation; consumed (persisted + cleared)
// in onTraceAllocated once forecastId is known. Was a shared `useRef` between submit and
// doForecast; now plain module state, since both live in this one file.
let draftAdoption: any = null;

// ── module helpers (moved verbatim from the hook) ────────────────────────────

// uid: random unique id with a graceful fallback (mirrors App.jsx's module-level
// helper verbatim — crypto.randomUUID() is unavailable in non-secure contexts).
const uid = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.floor(Math.random() * 1e9)}`;

// Past-date guard (pure). A resolution date already behind "today" can't be
// forecast. True when the resolution moment falls on a day BEFORE today — a
// resolution set to today is still allowed (it resolves later today). Accepts a
// date-only "YYYY-MM-DD" (parsed as LOCAL midnight via parseResolveDateLocal) or
// a full ISO timestamp.
// Reduce any input to its calendar-date portion before parsing as LOCAL midnight.
// The guard is DAY-based; parseResolveDateLocal treats a full ISO timestamp
// (e.g. "2026-07-08T00:00:00Z") as an instant, so in a negative-UTC-offset
// timezone the local Y/M/D would slip to the prior day and misjudge/misreport the
// resolution date. Slicing to "YYYY-MM-DD" pins the calendar day consistently
// (date-only strings already pass through parseResolveDateLocal unchanged).
const toResolveDay = (dateLike: any) =>
  parseResolveDateLocal(typeof dateLike === "string" ? dateLike.slice(0, 10) : dateLike);
// `exact` is the instant the server RESOLVED, when it resolved one. Given it, the
// guard is an instant comparison instead of a day comparison — a deadline half an
// hour away must not be rejected because its UTC date already rolled over. A
// 00:30 Tokyo deadline on 1 October serializes as 2026-09-30T15:30Z, and the day
// rule read that as "yesterday" for every Tokyo asker between midnight and 00:30.
//
// Without an exact instant the DAY rule stands unchanged, and that is deliberate:
// a date-only deadline is stored as its own midnight, so comparing it as an
// instant would reject every same-day forecast from the moment the day began —
// exactly what the day rule exists to allow.
export const isPastResolution = (dateLike: any, exact?: Date | null) => {
  if (exact && !isNaN(exact.getTime())) return exact.getTime() < Date.now();
  const d = toResolveDay(dateLike);
  if (!d || isNaN(d.getTime())) return false;
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const resDay = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  return resDay < startOfToday;
};
// Chat copy shown when a forecast is rejected for a past resolution date.
// Takes the same `exact` as the guard so the message names the day the guard
// actually judged. Reporting the sliced UTC day after rejecting on an instant
// would tell a Tokyo asker their 1 October deadline was "September 30".
const pastResolutionMessage = (dateLike: any, exact?: Date | null) => {
  const d = exact && !isNaN(exact.getTime()) ? exact : toResolveDay(dateLike);
  const dstr =
    d && !isNaN(d.getTime())
      ? d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
      : "in the past";
  return `That question's resolution date (${dstr}) has already passed. Forecasts can only be made for the future — please ask again with a resolution date and time that's still upcoming.`;
};

// LLM fallback for the timeframe gate. hasTimeframeHint is a fast regex that only
// catches EXPLICIT timeframes (years, months, "by 2027", holidays). It misses
// questions whose resolution date is KNOWABLE but implicit — "after Trump leaves
// office" (a fixed term end), "after the next US election". Rather than bounce
// those with a "needs a timeframe" message, ask the model whether a resolution
// date can be determined; only reject when it genuinely can't. Fails CLOSED
// (false on any error/timeout) so a flaky LLM degrades to the strict regex gate,
// never to a random-date forecast.
async function hasInferableTimeframe(question: string, turnId: string | null = null) {
  // Bound the check: a slow/hung LLM must not leave the submit stuck in its
  // loading state. Abort after 8s → the catch fails closed to the strict gate.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 8000);
  try {
    const prompt = `A user asked this forecasting question: "${question}"\n\nCan a specific resolution date or deadline be determined for it — even if the timeframe is IMPLICIT? Knowable-but-implicit examples: "after Trump leaves office" (his term ends on a fixed date), "after the next US presidential election", "by the next iPhone launch". NOT knowable: open-ended questions with no natural end, e.g. "will Pittsburgh's population grow" or "is the stock market overvalued".\n\nReply with ONLY one word: YES or NO.`;
    const text = await callLlm(prompt, { signal: controller.signal, turnId });
    // Require an explicit LEADING "yes" token, not just a substring anywhere —
    // so a non-compliant answer like "yesterday…" can't pass. Anything else
    // (incl. "no", empty, prose) is treated as NOT inferable.
    return /^\s*yes\b/i.test(text || "");
  } catch {
    return false; // timeout/abort/error — fail closed, degrade to the strict regex gate
  } finally {
    clearTimeout(timer);
  }
}

// ── postSubmit ───────────────────────────────────────────────────────────────

/**
 * Post-completion side effects (zoom, animation, series auto-populate) — moved verbatim
 * from the hook's `postSubmit` useCallback. Completion reconciliation happens for every
 * run before this focus-gated UI tail, so compare/background lanes cannot retain stale anchors.
 */
function clearSlideCacheOwner(slideCache: Ref, ownerId: string): void {
  const prefix = `${ownerId}::`;
  for (const key of Object.keys(slideCache.current)) {
    if (key.startsWith(prefix)) delete slideCache.current[key];
  }
}

/** Reconcile a run's typed nodes before the authoritative completion replaces them. */
function reconcileCompletion(deps: SubmitDeps, nfc: any): boolean {
  const progressDoc = nfc.id ? deps.store.getState().runsById[nfc.id]?.progressDoc : null;
  const liveEvents = progressDoc ? projectLiveEvents(progressDoc) : [];
  const reconciled =
    (deps.refs.forecastLiveProgressEnabledRef?.current ??
      deps.ui.forecastLiveProgressEnabled === true) &&
    liveEventsReconcileInPlace(liveEvents, nfc.events ?? []);
  if (!reconciled && nfc.id) clearSlideCacheOwner(deps.refs.slideCache, nfc.id);
  return reconciled;
}

function postSubmit(deps: SubmitDeps, nfc: any, reconciledLiveEvents: boolean): void {
  const { blinkContract, autoZoom, startForecastAnim, fetchLiveSeries } = deps.callbacks;
  const { setForecastingPhase, series } = deps.ui;
  blinkContract();
  if (nfc.contract?.resolution_date) autoZoom(nfc.contract.resolution_date);
  // postSubmit always follows the awaiting phase, which already drew the spine. Pass
  // skipPastSweep so the past-spine div doesn't re-animate the same sweep on remount.
  if (!reconciledLiveEvents) {
    startForecastAnim((nfc.events || []).length, { skipPastSweep: true });
  }
  setForecastingPhase(null);
  const newResDate = nfc.contract?.resolution_date || null;
  // Re-fetch any already-selected series with the new resolution date so their
  // projections extend out to the forecast line (rather than the server's default
  // 3-year-from-past[last] cap, which can fall short for sparse series like
  // Life Expectancy or Tuition CPI).
  // Thread each entry's `ssid` through: a signals-service signal can ONLY be
  // re-fetched by id (its name is an agent-authored display label the legacy
  // resolver can't resolve), so dropping it here 404s the series and silently
  // blanks a line the user had just plotted.
  if (newResDate) {
    Object.entries(series).forEach(([name, s]) =>
      fetchLiveSeries(name, s, newResDate, (s as { ssid?: string | null })?.ssid ?? null),
    );
  }
}

// ── doForecast ────────────────────────────────────────────────────────────────

/**
 * Run forecast with timing (shared by submit and edit-resubmit) — moved verbatim from
 * the hook's `doForecast` useCallback. The §7 #2 abort arm-order and shared-compare-signal
 * invariants live here; this is the path Lane A's command suite exercises directly.
 */
export async function doForecast(rawDeps: SubmitDeps, args: DoForecastArgs): Promise<void> {
  // The run follows the intent that started it (P2 design §4.6): the submit's own deps when
  // it calls in directly, else the intent attached to the pending run (a held forecast
  // started from the preprocess panel, an edit, a resolution pick).
  const runIntentId =
    (rawDeps as { intentId?: string | null }).intentId ||
    (args.pendingId ? intentEmitter().intentIdForRun(args.pendingId) : null) ||
    "";
  const deps = bindIntent(rawDeps, { id: runIntentId });
  const { store, services } = deps;
  const dispatchRun = store.dispatch;
  const { genForecastFromServer } = services.forecastClient;
  const {
    setLoading,
    setForecastingPhase,
    setActiveTrace,
    setLastObs,
    setForecastEngine,
    compareMode,
    compareEngine,
    triggerLlm,
    compareTriggerLlm,
    debugMode,
    forecastLiveProgressEnabled,
  } = deps.ui;
  const { forecastAbortRef, pendingEngineRevertRef, postTourTipRef, postTourTipRunRef } = deps.refs;
  const { guardForTos } = deps.callbacks;
  const { getTourFixture, getTourBranchIncrease } = deps.tour;
  const { q, engine, preprocessed, pendingId, msgId = null, routing = null } = args;

  // Arm the AbortController BEFORE awaiting guardForTos. Otherwise there's
  // a gap where the caller has entered doForecast but ac isn't installed
  // on forecastAbortRef yet, so resetForecast() (CLEAR) can't abort the
  // run via this ref. CLEAR would still reach submitAbortRef, but
  // doForecast doesn't read that — the run would resume after guardForTos
  // and reinsert a row the user thought they'd discarded. Arming up-front
  // means CLEAR aborts `ac` directly; the post-guardForTos check below
  // bails out before any state work.
  const ac = new AbortController();
  forecastAbortRef.current = ac;
  let claimedRunId: string | null = null;
  // Set by the catch when recovery ended unanswered; handed off once this run has let go.
  let stillRunningRunId: string | null = null;
  // Paced intro for the opening ladder steps (Preprocessing→Queued→Planning): the
  // backend blows through them in <1s so the line jumps straight to Researching.
  // Started when the run goes RUNNING; while it's active the latest real frame is held
  // and applied at hand-off, so it neither jumps ahead nor is lost. Stopped in the finally. (Single-forecast path only.)
  // Held in a ref (not a `let`) so its type reads consistently across the callbacks
  // that assign/read it — a bare `let` assigned only inside closures mis-narrows.
  const introRef: Ref<IntroPacer | null> = { current: null };
  // Pre-forecast ToS guard: surface the gate if the ToS is stale.
  if (!(await guardForTos())) {
    setLoading(false);
    if (pendingId) {
      intentEmitter().endRun({ runId: pendingId }, "cancelled");
      dispatchRun(runRemoved(pendingId));
    } // remove ToS-guard-rejected PREPROCESSING run from store
    setForecastingPhase(null);
    if (forecastAbortRef.current === ac) {
      forecastAbortRef.current = null;
    }
    return;
  }
  // CLEAR landed during guardForTos — bail out before we touch state.
  // resetForecast() already tore down active/forecasts/phase; continuing
  // would re-prepend the placeholder row via the success-path setters.
  if (ac.signal.aborted) {
    if (pendingId) intentEmitter().endRun({ runId: pendingId }, "cancelled");
    if (forecastAbortRef.current === ac) {
      forecastAbortRef.current = null;
    }
    return;
  }
  setLoading(true);
  const shouldDebug = compareMode || debugMode;
  // Hand the trace id from the SSE response headers up to React state
  // so the Cancel button (rendered conditionally on activeTrace) can
  // target the in-flight forecast. Also stamp the forecastId onto the
  // active forecast (and the matching forecasts[] entry) so terminal
  // setMsgs sites can call persistMessage(forecastId, …) without
  // chasing a separate ref. Finally, persist the user's submission
  // question now that we have a forecastId to attach it to — the
  // optimistic user msg in submit() pre-dated traceAllocated so it
  // couldn't be persisted at that point.
  // Shared per-trace-allocation persistence for BOTH the single forecast and each
  // compare leg: append the user question stamped with the run's createdAt (so it
  // sorts above the run-projected "Generating…" line, spec §5.3) and persist it;
  // then, for the leg that adopted the pre-forecast draft introduction (single, or
  // the compare PRIMARY), persist + clear those intro entries so a reload shows the
  // full thread, then any line filed to the run while it had no row. One path so a fix
  // can't drift between the two — it did once: intro persistence was single-only,
  // dropping the intro on a compare reload.
  const persistRunOnTrace = (
    forecastId: any,
    clientId: any,
    { metadata, adoptDraft }: { metadata?: any; adoptDraft?: boolean } = {},
  ) => {
    const run = store.getState()?.runsById?.[clientId] ?? null;
    // Compare persists the question once per leg, so one msg_id can sit on two rows.
    const md = msgId ? { ...metadata, msg_id: msgId } : metadata;
    const entry = makeEntry({
      role: "user",
      content: q,
      runId: clientId,
      ts: run?.createdAt,
      ...(md ? { metadata: md } : {}),
    });
    dispatchRun(entryAppended(run?.conversationId ?? clientId, entry));
    persistMessage(forecastId, entryToMessageBody(entry));
    if (adoptDraft && draftAdoption?.length) {
      const adopted = draftAdoption;
      draftAdoption = null;
      for (const e of adopted) persistMessage(forecastId, entryToMessageBody(e));
    }
    flushHeld(run?.conversationId ?? clientId, forecastId);
  };
  const onTraceAllocated = ({ traceId, forecastId, backend }: any) => {
    // Claim the main trace so a reload/restore/log-click can't open a second
    // stream for the same forecast in this tab. abortController is the same
    // controller forecastAbortRef holds, so registry.get(traceId) can abort it.
    // Only record the trace for release if we actually claimed it — otherwise
    // the terminal finally would release() an entry we don't own. traceId is
    // server-minted-unique so a clash is unexpected, but guard defensively.
    if (
      execTable.claim(pendingId, {
        kind: "main",
        clientId: pendingId,
        abortController: ac,
      })
    )
      claimedRunId = pendingId; // exec keyed by runId
    // Carry backend on the in-flight handle so a bug report filed mid-stream
    // (before the forecast completes and _obs exists) still records which
    // backend served it, rather than falling back to a stale/null lastObs.
    setActiveTrace({ traceId, forecastId, backend: backend || null });
    // Stamp BOTH traceId and forecastId onto the active forecast AND the
    // matching forecasts[] row. The log-click reattach path (line ~4454)
    // requires f.traceId to fire — without this stamp, clicking a still-
    // running row after CLEARing it falls through to the log-click reattach
    // path and the "generating" animation never resumes.
    // Only stamp active if the user is still viewing this forecast — if
    // they've navigated to another row, leave active alone (forecasts[]
    // still receives the trace stamp so the log-click reattach works).
    // Dual-write trace allocation to the store (write-once; first non-null wins per reducer).
    if (pendingId)
      dispatchRun(
        traceAllocated(pendingId, {
          traceId,
          forecastId: forecastId || null,
          backend: backend || null,
        }),
      );
    if (pendingId) dispatchRun(runStarted(pendingId)); // PREPROCESSING→RUNNING when the stream's trace allocates
    // Kick off the paced opening-steps intro now that the run is RUNNING (so the step
    // line renders). Real onProgress frames are deferred until it hands off.
    if (pendingId && !introRef.current)
      introRef.current = startProgressIntro(dispatchRun, pendingId, engine);
    // Single forecast: persist the question + adopted draft intro (shared path).
    // `&& pendingId` matches the other `if (pendingId)` guards in this handler and the
    // nullable `pendingId` type — a no-op in practice (the single-forecast path always
    // carries a real pendingId), but it keeps the null case from reaching entryAppended.
    if (forecastId && pendingId) persistRunOnTrace(forecastId, pendingId, { adoptDraft: true });
  };
  // Update the pending "Generating Forecast" chat message with the
  // current stage label so the user sees forecast-alive ticks every
  // ~5-30s instead of a static placeholder for 90+ seconds. _stageLabel
  // marks the msg so subsequent ticks overwrite the right one (avoids
  // stomping on unrelated pending msgs from branch generation / other
  // flows that also use _pending).
  //
  // Gate on `store.getState().activeRunId === pendingId` — same pattern as the
  // terminal handlers below. Without this, if the user clicks a sibling
  // running row that has its own "Generating Forecast" placeholder, this
  // (background) forecast's ticks would stomp the visible message for
  // the OTHER forecast the user is now viewing.
  const onProgress = (frame: any) => {
    const { stage, step, progress, data } = frame;
    // Progress is dispatched to the run store so selectChatProjection's
    // running event line animates (progress.message drives the spinner text). The
    // chat line is fully run-projected now — no msgs placeholder to rewrite.
    // step + data carry the fine phase (plan_built/agent_turn/…) and its counters
    // (turn/max_turns), which progressPayload resolves to the "Step N/X" line.
    // Unrecognized phases are ignored, and must be filtered BEFORE deferring: the intro
    // holds one frame, so an unusable one would displace a usable one.
    const payload = progressPayload(stage, progress, step, data, engine);
    if (payload.stepIdx < 0) return;
    // Defer real frames while the paced intro owns the line (opening steps): the latest
    // one is applied at hand-off (re-entering here with the intro inactive), so a fast
    // first frame is neither shown early nor dropped.
    if (introRef.current?.defer(() => !ac.signal.aborted && onProgress(frame))) return;
    if (pendingId) dispatchRun(runProgress(pendingId, payload));
  };
  // Typed frames enter the run reducer only behind the deployment flag.
  const onUpdateForRoot = (clientId: any) =>
    forecastLiveProgressEnabled && clientId
      ? (frame: unknown) => applyUpdateFrame(dispatchRun, store.getState, clientId, frame)
      : undefined;
  // Narration rides the same one flag as the rest of the live surface, so the
  // whole thing appears and disappears together.
  const onNarrationForRoot = (clientId: any) =>
    forecastLiveProgressEnabled && clientId
      ? (frame: unknown) => applyNarrationFrame(dispatchRun, store.getState, clientId, frame)
      : undefined;
  // Per-root progress: dispatch to each compare leg's run (design D5). Each leg
  // carries its OWN engine so the quick/deep step ladder matches that leg.
  const onProgressForRoot = (clientId: any, legEngine: any) =>
    makeProgressHandler(dispatchRun, clientId, legEngine);
  // Secondary compare leg's client id, hoisted so the catch can drop its live
  // running placeholder row on a thrown failure (it's only assigned inside the
  // compare branch). null for single-forecast runs.
  let compareLegClientId: string | null = null;
  // The compare legs this submission claimed in execTable, released with the single path's.
  const claimedLegIds: string[] = [];
  // Invocation ownership for the dual-leg reload store. A detached older pair may
  // settle after a newer pair has populated the same per-tab key, so terminal
  // cleanup must remove this pair by id rather than clear the store wholesale.
  let comparePairId: string | null = null;
  try {
    // Resolve the SECONDARY leg's engine BEFORE branching, so we can decide whether a
    // real comparison is even possible. Preference order:
    //   1) an explicit compareEngine the user picked (when it differs from primary),
    //   2) the OTHER tier's owner (quick↔deep flip),
    //   3) any other available forecaster.
    const allEngines = Object.keys(FORECASTER_INFO);
    let otherEngine = compareMode
      ? compareEngine
        ? compareEngine
        : (() => {
            const otherTag = effectiveTier(engine) === "quick" ? "deep" : "quick";
            const flipped = tagOwner(otherTag);
            return flipped && flipped !== engine && allEngines.includes(flipped)
              ? flipped
              : allEngines.find((e) => e !== engine) || engine;
          })()
      : null;
    if (compareMode) {
      dispatchRun(comparisonCleared()); // dual-write comparison clear (reset before seeding legs)

      // Two independent roots. Distinct, stable client_ids (even when pendingId is
      // absent) keep their lifecycle rows separate and key placeholders/progress/row
      // stamping; a null/null collision would leave the compare row never updated.
      const primaryClientId = pendingId || "fc-" + Date.now();
      const compareClientId = primaryClientId + "-cmp";
      compareLegClientId = compareClientId; // expose to the catch cleanup

      // Shared turn id so mergeThreads can collapse the duplicate user question
      // that each root persists — without it the merged compare chat shows the
      // question twice on reload.
      const compareQuestionTurnId = `cmp-${uid()}`;
      // Shared id tying the two legs' resume handles together so the mount effect
      // recognizes them as ONE pair (restore the comparison selection, reattach both).
      comparePairId = compareQuestionTurnId;
      // One birth timestamp per invocation. Trace allocation is per leg and may be
      // arbitrarily staggered, so its time cannot order compare generations reliably.
      const compareGenerationStartedAt = Date.now();
      const compareRecoveryDeadlineAt = compareGenerationStartedAt + FORECAST_RECOVERY_WINDOW_MS;

      // Root-aware trace handler: stamp the row by clientId; setActiveTrace ONLY for
      // the primary (cancel tracks one run); persist the question to each forecastId;
      // add each forecastId to the selection so the URL reflects the pair.
      const onTraceForRoot =
        (clientId: any, { primary, engine: legEngine }: any) =>
        ({ traceId, forecastId, backend }: any) => {
          if (ac.signal.aborted || forecastAbortRef.current !== ac) return;
          // Claim the leg like the single path's main run, so "this tab is executing it"
          // (execTable) holds for compare runs too: a reconnect then leaves it to its stream.
          if (execTable.claim(clientId, { kind: "compare", clientId, abortController: ac })) {
            claimedLegIds.push(clientId);
          }
          // Stamp traceId/forecastId/backend onto the compare leg's run so
          // reattach-by-click (checks f.traceId) and cancel/bug-report (use traceId)
          // work for running compare rows. Write-once: reducer ignores re-stamps.
          dispatchRun(
            traceAllocated(clientId, {
              traceId,
              forecastId: forecastId || null,
              backend: backend || null,
            }),
          );
          if (primary) setActiveTrace({ traceId, forecastId, backend: backend || null });
          if (forecastId) {
            // Same shared persistence as the single path; compare tags the turn id
            // and adopts the draft intro only on the PRIMARY leg.
            persistRunOnTrace(forecastId, clientId, {
              metadata: { compareTurnId: compareQuestionTurnId },
              adoptDraft: primary,
            });
            // Keep BOTH legs in the selection at all times — fall back to client ids
            // until each forecastId arrives. Without this the first leg to allocate
            // would shrink rootIds to one (the other's forecastId still null), drop
            // compareOn → hide the secondary lane until the slower leg lands.
            // Always client ids (runIds) — forecastIds must not leak into the store.
            dispatchRun(comparisonSet([primaryClientId, compareClientId]));
          }
          // Record THIS leg's reload-resume handle in the dual-leg store (genForecastFromServer
          // skips the single-slot key for pairLeg legs, so this is the only writer). Carries
          // everything the mount reattach effect needs: traceId to resume the stream, forecastId
          // to restore the comparison selection, clientId to key the placeholder row, the leg's
          // engine + preprocessed question to rebuild a contract before forecasts[] reloads, and
          // the shared pairId to recognize the two legs as one pair.
          // preprocessConfirmed:true — by the time a leg's trace allocates, the user has
          // ALREADY confirmed the Question→Contract preprocess for this submission (the
          // compare run only starts post-confirmation). Stamping it here makes the reload
          // reattach's `!pending.preprocessConfirmed` guard SUPPRESS a second preprocess
          // popup — without it the primary leg's reattach (seedChat:true) re-pops the panel
          // the user already dismissed. The preprocess should appear exactly once per submit.
          if (traceId)
            addComparePendingLeg({
              traceId,
              forecastId: forecastId || null,
              clientId,
              question: q,
              engine: legEngine,
              preprocessed: preprocessed || null,
              preprocessConfirmed: true,
              pairId: comparePairId,
              primary,
              generationStartedAt: compareGenerationStartedAt,
              submittedAt: Date.now(),
              recoveryDeadlineAt: compareRecoveryDeadlineAt,
            });
        };

      const timedFetch = async (eng: any, clientId: any, primary: any, triggerModel: any) => {
        const ft0 = Date.now();
        const fc = await genForecastFromServer(q, eng, null, true, preprocessed, ac.signal, {
          onTraceAllocated: onTraceForRoot(clientId, { primary, engine: eng }),
          onProgress: onProgressForRoot(clientId, eng),
          onUpdate: onUpdateForRoot(clientId),
          onNarration: onNarrationForRoot(clientId),
          onContractKey: (p: any) => {
            recordContractKey(p);
            if (clientId) dispatchRun(contractKeySet(clientId, p.key, p.key_status));
          },
          clientId,
          pairLeg: true,
          recoveryDeadlineAt: compareRecoveryDeadlineAt,
          postprocessModel: triggerModel || null,
          tourFixture: getTourFixture(),
          tourBranchIncrease: getTourBranchIncrease(),
        });
        fc._computeTime = (Date.now() - ft0) / 1000;
        return fc;
      };

      // Each compare lane's "Generating Forecast" line is now run-projected:
      // the two keyed runs created below (status:RUNNING) drive selectChatProjection's
      // per-lane running event lines, so no keyed chat placeholders are seeded here.

      // LIVE secondary lane: push a RUNNING placeholder row for BOTH legs into forecasts[]
      // at run-start. Previously the secondary leg only entered forecasts[] on COMPLETION
      // (the cfc upsert), so during the run compareRoots couldn't resolve compareFid →
      // compareOn was false → no secondary lane until it finished. With these placeholders
      // present (status:"running", _pending, keyed by clientId), compareRoots resolves both
      // immediately and the secondary lane renders live with the per-lane awaiting animation.
      // serverContext.metadata.orchestrator carries each leg's engine so rootEngine() shows
      // the right lane label mid-run. The completion cfc/nfc upsert reconciles by id, and the
      // abort/supersede sweep below removes them. Mirrors the reattach running placeholder.
      // Create two independent root runs in the store — one per compare leg, each with
      // its own distinct runId/conversationId. Primary enters as RUNNING (shared preprocess
      // already done); secondary likewise. Neither is a branch of the other.
      // The PRIMARY run was usually already seeded by submit()/editResubmit() under this same
      // runId (question + PREPROCESSING + a preprocessed contract via contractSet). Recreating
      // it here would clobber question/contract/createdAt and blank the primary lane until
      // completion — so when it already exists we only transition it RUNNING and write-once
      // stamp the backend (traceAllocated leaves traceId/forecastId null, stamps backend).
      // The rare no-pendingId path (no submit seed) still needs the run created.
      if (store.getState().runsById[primaryClientId]) {
        dispatchRun(runStarted(primaryClientId)); // PREPROCESSING→RUNNING, preserves question/contract
        dispatchRun(traceAllocated(primaryClientId, { backend: engine })); // write-once backend stamp
      } else {
        dispatchRun(
          runCreated(
            makeRun({
              runId: primaryClientId,
              conversationId: primaryClientId,
              question: q,
              backend: engine,
              status: STATUS.RUNNING,
              triggerModel: effectiveTriggerModel(triggerLlm),
            }),
          ),
        );
      }
      // The secondary leg forecasts the SAME question with the other engine, so seed it
      // by INHERITING the primary run's question + preprocessed contract — not doForecast's
      // local `q`, which is the preprocessed/revised phrasing and would diverge from the
      // primary's originally-seeded question (the two log rows then show different wording
      // for one question). Without these the secondary run carries no question (Forecast Log
      // shows "Unnamed") and no contract (no resolution_date → "Resolves —"; the secondary
      // lane has no resolution marker while running). Both legs share ONE preprocess; each
      // leg's probability diverges only on completion. Falls back to `q` if the primary run
      // somehow lacks a question (rare no-pendingId path, where it's seeded with `q` too).
      const _primaryRun = store.getState().runsById[primaryClientId];
      dispatchRun(
        runCreated(
          makeRun({
            runId: compareClientId,
            conversationId: compareClientId,
            question: _primaryRun?.question || q,
            backend: otherEngine,
            status: STATUS.RUNNING,
            triggerModel: effectiveTriggerModel(compareTriggerLlm),
          }),
        ),
      );
      if (_primaryRun?.contract) dispatchRun(contractSet(compareClientId, _primaryRun.contract));
      // Seed the selection so compareRoots resolves to the two running rows immediately
      // (onTraceForRoot will re-set it with the real forecastIds once traces allocate, but
      // those are the SAME client ids until then — compareRoots matches on id OR forecastId).
      dispatchRun(comparisonSet([primaryClientId, compareClientId])); // dual-write seed selection

      // Kick off both legs, then await together. They share `ac.signal`, so on
      // one leg's failure we abort `ac` to cancel the sibling's in-flight request
      // — Promise.all rejects on the first failure but does NOT stop the other
      // promise, which would otherwise stream a full forecast in the background.
      // The no-op catches keep the sibling's resulting (Abort) rejection from
      // surfacing as an unhandled rejection once Promise.all has already settled.
      const pPrimary = timedFetch(engine, primaryClientId, true, triggerLlm);
      const pCompare = timedFetch(otherEngine, compareClientId, false, compareTriggerLlm);
      pPrimary.catch(() => {}); // swallow-ok: cleanup — awaited by Promise.all below
      pCompare.catch(() => {}); // swallow-ok: cleanup — awaited by Promise.all below
      let primaryFc: any, compareFc: any;
      try {
        [primaryFc, compareFc] = await Promise.all([pPrimary, pCompare]);
      } catch (e) {
        if (!ac.signal.aborted) ac.abort();
        throw e;
      }
      // Both legs settled — sweep only this invocation. Each leg also removes its own
      // entry on clean terminal inside genForecastFromServer; the pair-level sweep
      // catches a partially-removed old pair without deleting a newer pair's handles.
      removeComparePendingPair(comparePairId);
      // Void the pair ONLY if its OWN controller was aborted — edit-resubmit / cancel tore
      // it down (both call ac.abort()). Do NOT bail merely because a newer forecast took the
      // shared forecastAbortRef (CLEAR is a DETACH, not a cancel): a backgrounded pair must
      // still FINALIZE both legs via the runCompleted dispatches below, or the primary leg
      // stays stuck "running" and the secondary vanishes — the compare sibling of bug 3f27125a.
      // (A genuine abort rejects Promise.all above and never reaches here; this guard's live
      // role is the abort-just-as-both-legs-resolved race, where we still drop the secondary
      // placeholder and bail. The focus-sensitive activeSet/postSubmit below is already gated
      // on activeRunId===pendingId, and the terminal UI teardown is ownership-gated, so a
      // background pair finalizing can't clobber the newer run's foreground state.)
      if (ac.signal.aborted) {
        // No keyed chat placeholders to sweep (the running lines are run-projected and clear
        // when the runs are removed/terminated). Drop the SECONDARY running placeholder row
        // we pushed for the live lane (this is a clean early-return, so the outer catch's
        // pendingId cleanup never runs; the primary row is the user's pendingId row, handled
        // on the exception path, but the compare row would otherwise linger as a stuck lane).
        dispatchRun(runRemoved(compareClientId)); // remove aborted secondary compare placeholder from store
        return;
      }
      primaryFc._engine = engine;
      compareFc._engine = otherEngine;

      recordUiEvent({
        event: "ui.forecast.result",
        engine,
        traceId: primaryFc._obs?.traceId || null,
        backend: primaryFc._obs?.backend || null,
        latencyMs: primaryFc._obs?.latencyMs || null,
        probability: primaryFc.contract?.probability ?? null,
        compareEngine: otherEngine,
        compareTraceId: compareFc._obs?.traceId || null,
      });

      const pid = primaryFc._obs?.forecastId || null;
      const cid = compareFc._obs?.forecastId || null;
      const nfc = { ...primaryFc, id: primaryClientId, status: "completed", forecastId: pid };
      const cfc = { ...compareFc, id: compareClientId, status: "completed", forecastId: cid };
      // Reconcile BOTH compare legs before either authoritative completion lands. The
      // visible post-submit effects remain primary-only, but a secondary typed lane owns
      // cached anchors too and must not retain a same-ID node whose timing moved.
      const primaryReconciled = reconcileCompletion(deps, nfc);
      reconcileCompletion(deps, cfc);
      // Dual-write completion for each compare leg (closes the RUNNING state).
      // Store selection was seeded with client ids at run-start and is already correct —
      // do NOT "upgrade" to forecastIds here, as runsById is keyed by client ids.
      dispatchRun(
        runCompleted(primaryClientId, {
          contract: nfc.contract,
          serverPrediction: primaryFc.serverPrediction,
          events: nfc.events || [],
          serverContext: nfc.serverContext || null,
          forecastId: pid ?? undefined,
          partial: primaryFc._partial,
          debug: primaryFc._debug,
          obs: primaryFc._obs,
          serverExplanation: primaryFc.serverExplanation,
          report: primaryFc.report,
        }),
      );
      dispatchRun(
        runCompleted(compareClientId, {
          contract: cfc.contract,
          serverPrediction: compareFc.serverPrediction,
          events: cfc.events || [],
          serverContext: cfc.serverContext || null,
          forecastId: cid ?? undefined,
          partial: compareFc._partial,
          debug: compareFc._debug,
          obs: compareFc._obs,
          serverExplanation: compareFc.serverExplanation,
          report: compareFc.report,
        }),
      );
      // (no comparisonSet here — client-id selection from the seed at ~5261 is already correct)

      // The per-root result line ("Prediction: X%") is RUN-PROJECTED —
      // selectChatProjection emits it from each completed run's contract.probability,
      // so it is NOT persisted as a dialogue message here (it would double up with the
      // projected line on reload). The runCompleted dispatches above are the record.
      if (store.getState().activeRunId === pendingId) {
        dispatchRun(activeSet(primaryClientId)); // dual-write compare primary result active to store
        postSubmit(deps, nfc, primaryReconciled);
      }
    } else {
      const fc = await genForecastFromServer(
        q,
        engine,
        null,
        shouldDebug,
        preprocessed,
        ac.signal,
        {
          onTraceAllocated,
          onProgress,
          onUpdate: onUpdateForRoot(pendingId),
          onNarration: onNarrationForRoot(pendingId),
          onContractKey: (p: any) => {
            recordContractKey(p);
            if (pendingId) dispatchRun(contractKeySet(pendingId, p.key, p.key_status));
          },
          clientId: pendingId,
          routing,
          tourFixture: getTourFixture(),
          tourBranchIncrease: getTourBranchIncrease(),
        },
      );
      // The stream is over: discard any frame the intro still holds so it can't land
      // on the run after its result.
      introRef.current?.stop();
      // Void this run entirely ONLY if its OWN controller was aborted — i.e. the user
      // edited the preprocessed question and resubmitted, or cancelled it. Both tear the
      // run down by aborting `ac` (edit-resubmit + cancel call ac.abort()), so a late
      // result can't resurrect the removed run or clobber the replacement in flight.
      if (ac.signal.aborted) {
        return;
      }
      // Does this run still own the foreground in-flight slot? It does NOT when the user
      // started ANOTHER forecast while this one streamed in the background — CLEAR is a
      // DETACH, not a cancel, so this run's stream keeps going and MUST land in the log.
      // Starting the newer forecast overwrote forecastAbortRef with the newer run's
      // controller WITHOUT aborting this one. A background run still FINALIZES
      // unconditionally (runCompleted below), but every ephemeral foreground-UI write on
      // this terminal path is gated on ownership — otherwise it would clear the newer
      // run's Cancel button / spinner / focus out from under it. (Previously the guard
      // above voided the whole run when a newer forecast merely took the shared abort ref,
      // so background forecasts stayed stuck "running" and never streamed in — bug 3f27125a.)
      const ownsForeground = forecastAbortRef.current === ac;
      // Reject a past resolution date the SERVER settled on — the preprocess
      // guard misses vague timeframes ("Summer 2026") because the forecast
      // server, not preprocess, resolves them to a concrete (here already-past)
      // date. Don't land it; explain in chat (draft conv, as above) and remove
      // the run so no past forecast enters the log. The chat explanation + foreground
      // teardown only fire when the user is still on this run (ownsForeground); the
      // runRemoved is unconditional so an invalid background run can't linger in the log.
      if (isPastResolution(fc.contract?.resolution_date)) {
        if (ownsForeground) {
          dispatchRun(
            entryAppended(
              DRAFT_CONV_ID,
              makeEntry({
                role: "assistant",
                content: pastResolutionMessage(fc.contract?.resolution_date),
              }),
            ),
          );
          setForecastingPhase(null);
          setLoading(false);
          dispatchRun(activeSet(null));
        }
        if (pendingId) dispatchRun(runRemoved(pendingId));
        return;
      }
      // lastObs feeds mid-stream bug reports for the forecast the user is WATCHING; a
      // background run finishing must not overwrite it with its own obs.
      if (ownsForeground && fc._obs) setLastObs({ ...fc._obs, ts: new Date().toISOString() });
      recordUiEvent({
        event: "ui.forecast.result",
        engine,
        traceId: fc._obs?.traceId || null,
        backend: fc._obs?.backend || null,
        latencyMs: fc._obs?.latencyMs || null,
        probability: fc.contract?.probability ?? null,
      });
      const fid = fc._obs?.forecastId || null;
      const nfc = {
        ...fc,
        id: pendingId || "fc-" + Date.now(),
        status: "completed",
        forecastId: fid,
      };
      // A successful forecast clears the consecutive-error streak so the next failure
      // (if any) starts the "try again" nudge fresh rather than jumping to "submit a bug".
      if (deps.refs.forecastErrorStreakRef) deps.refs.forecastErrorStreakRef.current = 0;
      // This must run even for a detached/background completion. `postSubmit` is
      // focus-gated below, but the row can be reopened later and must not reuse a live
      // trigger's stale anchor after an authoritative timing revision.
      const reconciledLiveEvents = reconcileCompletion(deps, nfc);
      // runCompleted is UNCONDITIONAL — the run must finalize into the store/log
      // even if the user has detached it (CLEAR) or navigated to another forecast.
      if (pendingId) {
        dispatchRun(
          runCompleted(pendingId, {
            contract: nfc.contract,
            serverPrediction: fc.serverPrediction,
            events: nfc.events || [],
            serverContext: nfc.serverContext || null,
            forecastId: fid ?? undefined,
            partial: fc._partial,
            debug: fc._debug,
            obs: fc._obs,
            serverExplanation: fc.serverExplanation,
            report: fc.report,
          }),
        );
      }
      // Focus + visible chat (activeSet, postSubmit) update ONLY when the user
      // still has this run active. Otherwise a detached (CLEAR) or navigated-away
      // forecast would steal the chat back on completion — the row just fills in
      // silently in the log. Mirrors the guarded compare path above.
      if (store.getState().activeRunId === pendingId) {
        dispatchRun(activeSet(pendingId)); // re-affirm active (already pendingId during a normal run)
        const fireTip = postTourTipRef.current;
        if (fireTip) postTourTipRef.current = false;
        if (fireTip) postTourTipRunRef.current?.();
        // Deferred add-your-own-trigger reminder. Counted here rather than at submit so
        // it tracks forecasts the user actually SAW land, and gated on the tour fixture
        // being absent so the tour's own example run is not one of "their" forecasts.
        // claimNudge is one-shot and self-closing; it returns true at most once ever.
        if (!getTourFixture()) {
          recordForecastLanded();
          if (claimNudge()) {
            deps.refs.pushSayerMsgRef.current?.(CUSTOM_TRIGGER_REMINDER, {
              kind: "tour",
              target: chatTargetForRun(pendingId),
            });
          }
        }
        postSubmit(deps, nfc, reconciledLiveEvents);
      }
      // Post the forecast summary into the run's OWN conversation with a "Full Report"
      // link — NOT gated on the run still being active (outside the tutorial; see the
      // tour carve-out below). Its target is THIS run's conversation (not the live one),
      // so it appears when the user returns to the forecast even if
      // they'd navigated away by the time it completed (previously it was skipped
      // entirely and never showed on click-back).
      // Only when the backend produced a structured summary; the reportLink flag drives
      // the link render and survives reload via the message round-trip. The projected
      // "Forecast: X%" line (below) coexists with it. Gated on hasForecastSummaryMessage
      // so a run that ALSO finalizes via the resume/reattach path (which posts the same
      // summary — see resumeForecasts.ts) can't double-post: exactly once from any path.
      // Suppress this auto-summary DURING THE TUTORIAL: the tour has its own scripted
      // narration and the example forecast's summary line would clutter the guided
      // console (and, as a non-guide entry, would survive reload past
      // suppressGuideNarration). getTourFixture() is truthy only while the tour is
      // driving the scripted forecast, so real user forecasts still post their summary.
      // The completion post is the report's ASSESSMENT plus the "Full Report →" opener —
      // the same shape for binary and multiway, and the same thing the resume path posts
      // (resumeForecasts.ts already used assessment-only; submit was the odd one out).
      //
      // It deliberately does NOT restate the distribution. Multiway used to fall back to
      // `multiwaySummaryText`, printing the per-bucket breakdown into the chat — but the
      // bars on the timeline already show exactly that, so the chat spent its most
      // valuable slot repeating a picture the user was looking at, instead of the prose
      // that says what the number MEANS. The assessment is the thing chat can add.
      //
      // The empty-assessment case still has to post SOMETHING, and that is not a style
      // choice: `reportLink: true` renders the only affordance a multiway result has for
      // opening its report (the resolution pie that also opens it never draws for multi).
      // Today no multiway report carries `forecast_summary` at all, so dropping the post
      // when the assessment is missing would strand every multiway forecast with no route
      // to its own report. Hence a bare opener line rather than nothing — and rather than
      // the distribution reprint it replaces.
      const assessment = assessmentText(nfc);
      const summary = assessment || (extractMultiway(nfc.contract) ? "Forecast complete." : null);
      if (summary && !getTourFixture() && !hasForecastSummaryMessage(store.getState(), pendingId)) {
        deps.refs.pushSayerMsgRef.current?.(summary, {
          // Keyed on the run, not just its forecastId: the buffered (auth-off) path never
          // allocates a forecastId, and the post then followed whatever forecast was on screen.
          target: chatTargetForRun(pendingId, nfc.forecastId ?? null),
          reportLink: true,
        });
      }
      // The "Prediction: X%" line is run-projected
      // from the completed run via selectChatProjection (runCompleted above is the record).
    }
  } catch (e: any) {
    // If a NEWER doForecast has taken over (forecastAbortRef.current is no longer our `ac`),
    // skip the cleanup — the new run owns the awaiting state and the pending log entry now.
    const stillOwned = forecastAbortRef.current === ac;
    const userOnThis = store.getState().activeRunId === pendingId;
    const recoveryWaitExpired = isRecoveryWaitExpiredError(e);
    const resultPending = e.code === "forecast_result_pending";
    // Recovery that ended on SILENCE — a `/result` read that stalled through every bounded
    // attempt, or a request that died without a status — with the pending handle kept (the
    // client's "not terminal" mark). The forecast is still running server-side, so this is
    // not an error: no runErrored, no streak bump, no persisted "submit again" line. Narrower
    // than `_preservePendingHandle` alone on purpose: a resume `error` frame or an HTTP refusal
    // also keeps the handle, but those ARE answers and still error the run below.
    const stillResumable =
      e.name !== "AbortError" && !!e._preservePendingHandle && isUnansweredRecoveryError(e);
    if (stillResumable) {
      recordUiEvent({
        event: "ui.forecast.recovery_unanswered",
        engine,
        code: e.code ?? null,
        message: String(e.message || e).slice(0, 300),
      });
      if (pendingId) stillRunningRunId = pendingId;
    } else if (e.name !== "AbortError" && !recoveryWaitExpired && !resultPending) {
      recordUiEvent({
        event: "ui.forecast.error",
        engine,
        message: String(e.message || e).slice(0, 300),
      });
      // The error line is run-projected from the ERRORED run via
      // selectChatProjection (it renders run.error as terminal content).
      // Write error to the store (retain-on-terminal — errored runs are kept for log display).
      if (pendingId) dispatchRun(runErrored(pendingId, e));
      // Dual-write error for the compare leg run when it was created (compareLegClientId set
      // after the two runCreated dispatches, so it's only present in the compare branch).
      if (compareLegClientId) dispatchRun(runErrored(compareLegClientId, e));
      // Sayer console retry nudge, escalating with the consecutive-error streak: the
      // first failure asks the user to try again; a second (or later) failure in a row
      // suggests filing a bug. Reset to 0 on any success (above). Only posts when the
      // user is still viewing this run, so it can't hijack a different conversation.
      const streakRef = deps.refs.forecastErrorStreakRef;
      if (streakRef) {
        streakRef.current = (streakRef.current || 0) + 1;
        if (userOnThis) {
          deps.refs.pushSayerMsgRef.current?.(
            streakRef.current >= 2
              ? "This keeps failing on Sayer's side. Please send feedback from the menu so the team can look into it."
              : "Ask your question again to retry. If it fails again, send feedback and we'll look into it.",
            { target: chatTargetForRun(pendingId) },
          );
        }
      }
    }
    // Pair-store ownership is independent of the foreground UI slot. A detached old
    // pair can fail terminally after a newer controller takes over; remove the old
    // pair by id while preserving both newer handles. Failed recovery and reload/cancel
    // aborts remain resumable and intentionally keep their handles.
    if (e.name !== "AbortError" && !e._preservePendingHandle && compareMode && comparePairId) {
      removeComparePendingPair(comparePairId);
    }
    if (stillOwned) {
      // Only stop the visible animation if the user is on this forecast —
      // otherwise they're on something else with its own forecastingPhase.
      if (userOnThis) setForecastingPhase(null);
    }
  } finally {
    // Clear any pending intro-pacer timers on EVERY exit (success/error/abort).
    introRef.current?.stop();
    // Release the registry claim on EVERY exit — including the post-stream
    // superseded/aborted early returns inside the try (which bypass the
    // cleanup below). Leaking it would leave the trace status:"streaming"
    // forever and block future claim()/reattach for it. Released here only;
    // the foreground-UI teardown below is ownership-gated so a background run
    // that finishes after the user started a newer forecast doesn't clear the
    // newer run's in-flight UI state.
    if (claimedRunId) execTable.release(claimedRunId); // exec keyed by runId
    for (const id of claimedLegIds) execTable.release(id);
  }
  // Foreground-UI teardown on a terminal exit — ONLY when this run still owns the
  // in-flight slot (its controller is the one forecastAbortRef holds). A background run
  // superseded by a newer forecast leaves all of this alone: clearing the active trace /
  // loading / one-shot engine swap here would clobber the newer run the user is now
  // watching. (Cancel + edit-resubmit clear their own UI and both null this ref, so they
  // don't depend on this block. Formerly these ran unconditionally, relying on the now-
  // removed takeover guard to early-return background runs before reaching here.)
  if (forecastAbortRef.current === ac) {
    forecastAbortRef.current = null;
    // Clear active-trace tracking so the Cancel button hides once nothing's left to cancel.
    setActiveTrace(null);
    setLoading(false);
  }
  // Restore the previous engine if this run was a one-shot chat-driven swap — on ANY
  // terminal exit, INCLUDING cancel / edit-resubmit. Those paths abort `ac` and null
  // forecastAbortRef *before* doForecast reaches here, so they fall outside the
  // ownership gate above; gating the revert on ownership would strand the engine on the
  // temporary one-shot value after a cancelled swapped run. Unlike the trace/loading
  // teardown, this is safe to run unconditionally: it only mutates the engine SELECTOR
  // for the NEXT forecast (never an in-flight run's already-captured engine), so a
  // superseded background run reverting can't disturb the newer run's foreground state.
  if (pendingEngineRevertRef.current) {
    const prev = pendingEngineRevertRef.current;
    pendingEngineRevertRef.current = null;
    setForecastEngine(prev);
  }
  // Hand a still-running run back to the resume path. LAST, after the `finally` released
  // this run's exec claim (reattach claims it afresh and no-ops while it is held) and after
  // the foreground teardown above (the primary reattach re-arms the awaiting phase itself).
  if (stillRunningRunId) handOffStillRunning(deps, stillRunningRunId);
}

/**
 * Hand a run whose live recovery ended unanswered back to the resume path. A single run is
 * reattached through the entry point a log click uses: in the foreground while the user still
 * has it open (read live — the catch ran a whole recovery ago), which re-seeds its thread and
 * awaiting phase, and otherwise as a silent background leg that can't steal focus. A run this
 * tab won't reattach (a compare pair, whose handles a reload resumes, or no reattach wired)
 * instead gets a store-only notice, if on screen, saying it is still running.
 */
function handOffStillRunning(deps: SubmitDeps, runId: string) {
  const reattach = deps.callbacks.reattachRunning;
  const state = deps.store.getState();
  const run = state.runsById?.[runId];
  if (!run) return;
  const onScreen = state.activeRunId === runId;
  if (reattach && run.traceId && !deps.ui.compareMode) {
    try {
      void Promise.resolve(
        reattach(
          {
            traceId: run.traceId,
            forecastId: run.forecastId,
            clientId: runId,
            question: run.question || "",
            contract: run.contract || null,
            engine: run.backend || null,
          },
          null,
          onScreen ? {} : { lane: "secondary", seedChat: false },
        ),
      ).catch(() => {}); // swallow-ok: cleanup — reattach surfaces its own failures in chat + store
    } catch (_e) {
      /* as above — never let a reattach fault escape the submit path */
    }
    return;
  }
  // Store-only: deliberately NOT persistMessage'd, so a reload never resurrects it. (Not
  // posted on the reattach path above: its thread reload would replace it straight away.)
  if (onScreen) {
    deps.store.dispatch(
      entryAppended(
        run.conversationId ?? runId,
        makeEntry({ role: "assistant", content: STILL_RUNNING_RELOAD_MESSAGE, runId }),
      ),
    );
  }
}

// ── submitForecast ─────────────────────────────────────────────────────────────

/**
 * Submit: preprocess → show panel + start forecast in background simultaneously — moved
 * verbatim from the hook's `submit` useCallback, now `(deps, args)`. Delegates the run to
 * `doForecast(deps, …)`.
 */
export async function submitForecast(deps: SubmitDeps, args: SubmitArgs): Promise<void> {
  // The intent opens inside, once the submit is past its busy guard; a throw ends it here.
  let intent: IntentHandle<"forecast.submit"> | null = null;
  try {
    return await submitForecastUnder(deps, args, (h) => {
      intent = h;
    });
  } catch (e) {
    (intent as IntentHandle<"forecast.submit"> | null)?.end("error");
    throw e;
  }
}

/** Submit-flow exits that end the intent here; every other exit leaves it to the run. */
const SUBMIT_EXIT_OUTCOMES: Record<string, "error" | "cancelled"> = {
  rejected_multi_outcome: "error",
  rejected_no_timeframe: "error",
  past_resolution: "error",
  tos_blocked: "cancelled",
  aborted: "cancelled",
};

async function submitForecastUnder(
  rawDeps: SubmitDeps,
  args: SubmitArgs,
  onIntent: (h: IntentHandle<"forecast.submit">) => void,
): Promise<void> {
  const {
    input,
    loading,
    forecastEngine,
    compareMode,
    triggerLlm,
    multiwayEnabled,
    autoQuestionTypeEnabled,
    preprocessVariantId,
    setInput,
    setLoading,
    setForecastingPhase,
    setPendingPreprocess,
  } = rawDeps.ui;
  const {
    postTourNudgeTimerRef,
    preprocessConfirmedRef,
    submitAbortRef,
    contractNotePendingRef,
    lastTourPendingIdRef,
    slideCache,
    pushSayerMsgRef,
  } = rawDeps.refs;
  const { guardForTos, autoZoom, clearDraft, clearSeriesSelection } = rawDeps.callbacks;
  const { getTourFixture } = rawDeps.tour;
  const { overrideText, overrideEngine, force = false } = args;
  // Every `turn?.` call below is timing only; `msgId` is the one non-timing read.
  const turn = args.turn ?? null;

  // `force` bypasses the loading guard for an EXPLICIT new forecast (e.g. clicking
  // an example) — in-flight branches set the global `loading`, which would
  // otherwise silently drop the new forecast. The double-submit protection the
  // guard provides still applies to ordinary Enter-key submits (force=false).
  const rawInput = (typeof overrideText === "string" ? overrideText : input).trim();
  // The turn's message id only when this submit persists that message's own text; a
  // submit without a turn (example chips, the tour) or of other text (an engine rerun
  // of the active question, a confirmed pending question) is a message of its own.
  const msgId = turn?.msgId && turn.msgText === rawInput ? turn.msgId : newMessageId();
  // Question-type routing — decide which preprocess endpoint authors the contract. The
  // RUN downstream stays keyed purely on whether the returned contract carries an
  // `outcome_space`, so this only chooses the authoring path; what is sent to the forecast
  // server never changes.
  //   - `MW:` prefix (flag-gated) → force-multi: always authors a multi-value space.
  //   - `B:` prefix → force-binary: pin the ordinary yes/no path (an override for when
  //     auto-routing would otherwise classify the question as multi-valued).
  //   - otherwise, if auto question-type routing is on → the contract server CLASSIFIES
  //     binary vs categorical vs numeric itself (removes the need for a prefix).
  //   - otherwise → the plain binary path (today's `MW:`-only behaviour).
  // Precedence: explicit `MW:` beats explicit `B:` beats auto beats plain binary. The
  // matched prefix is stripped from the question; an unhonoured `MW:` (flag off) is left
  // in the text exactly as before.
  const routed = routePreprocess(rawInput, { multiwayEnabled, autoQuestionTypeEnabled });
  const preprocessMode = routed.mode;
  const forceBinary = routed.forced && preprocessMode === "binary";
  const q = routed.question;
  if (!q || (loading && !force)) {
    turn?.finish("ignored_busy");
    return;
  }
  // The user's forecast (P2 design §4.6): it follows the run it starts, wherever the user is
  // meanwhile; the exits below that start no run end it with their outcome.
  const pendingId = "fc-pending-" + uid();
  // The new-question page's box: a typed question, or an example asked unchanged.
  const askHeroOrigin =
    turn?.origin === "ask-hero.submit"
      ? "ask-hero.submit"
      : turn?.origin === "ask-hero.example"
        ? "ask-hero.example"
        : undefined;
  const from = getTourFixture()
    ? "tour"
    : turn?.fromExample
      ? "example"
      : askHeroOrigin
        ? "ask_hero"
        : turn
          ? "chat"
          : force
            ? "example"
            : "form";
  const origin =
    args.origin ??
    (from === "tour"
      ? "tour"
      : from === "ask_hero"
        ? askHeroOrigin
        : from === "chat"
          ? "chat"
          : turn?.fromExample
            ? "chat.example-question"
            : undefined);
  const intent = beginIntent(
    "forecast.submit",
    {
      forecast_id: pendingId,
      msg_id: msgId,
      from,
      engine:
        typeof overrideEngine === "string" && overrideEngine ? overrideEngine : forecastEngine,
    },
    {
      origin,
      // A chat message is sent from the chat (the new-question page's box from that page),
      // however long it was classified before this.
      surface:
        from === "ask_hero"
          ? "ask-hero"
          : origin === "chat" || origin === "chat.example-question"
            ? "chat"
            : undefined,
    },
  );
  onIntent(intent);
  intent.attachRun(pendingId);
  const deps = bindIntent(rawDeps, intent);
  const { store } = deps;
  const dispatchRun = store.dispatch;
  const finish = (reason: string) => {
    turn?.finish(reason);
    const outcome = SUBMIT_EXIT_OUTCOMES[reason];
    if (outcome) intent.end(outcome);
  };
  // Where a refusal below is filed: the chat the question was asked in (a chat turn's own
  // target), never whatever is selected once the timeframe check answers.
  const replyTarget = turn?.target ?? captureChatTarget(store.getState());
  // Binary-enforcement backstop: with the router off, a multi-outcome question isn't
  // forecastable (binary-izing it yields a nonsensical P(YES)) — nudge to a yes/no + date
  // instead. Skipped when the router is on or the user forced binary with `B:`. Deterministic
  // last line behind the flag-aware classifier, for anything that still reaches submit.
  if (!autoQuestionTypeEnabled && !forceBinary && looksLikeMultiForecastIntent(q)) {
    setInput(q);
    pushSayerMsgRef?.current?.(
      "That looks like a multi-outcome question — I can only forecast yes/no questions here. " +
        "Please rephrase it as a yes/no question with a clear resolution date, " +
        'e.g. "Will the Fed cut rates by December 2026?"',
      { target: replyTarget },
    );
    finish("rejected_multi_outcome");
    return;
  }
  // A submitted forecast cancels the post-tour "submit your own question" nudge.
  if (postTourNudgeTimerRef.current) {
    clearTimeout(postTourNudgeTimerRef.current);
    postTourNudgeTimerRef.current = null;
  }
  // The user submitting their OWN forecast ends the post-tour examples invitation
  // (so it won't re-show on a later reload). The tour's own fixture submit keeps it.
  if (!getTourFixture()) {
    try {
      sessionStorage.removeItem(POST_TOUR_EXAMPLES_KEY);
    } catch {
      /* ignore */
    }
  }
  // The chat can pass a per-call engine override (e.g. "redo this with deep") — falls back to settings.
  const engineToUse =
    typeof overrideEngine === "string" && overrideEngine ? overrideEngine : forecastEngine;
  // Reject questions with no time-frame hint at all — otherwise preprocess assigns a random
  // resolution date and the same question gets a different forecast each run. Be lenient:
  // accept years, months, quarters, seasons, weekdays, holidays (Halloween, Thanksgiving, …),
  // durations ("5 years"), relative phrases ("by next decade", "within a year"), and
  // date-shaped strings. Only ask the user to add a timeframe when none of these patterns
  // match. Holiday names without a year (e.g. "before Halloween") rely on preprocess to
  // resolve them to the next upcoming occurrence.
  const hasTimeframe = hasTimeframeHint(q);
  if (!hasTimeframe) {
    // The fast regex found no EXPLICIT timeframe — but the resolution date may
    // still be KNOWABLE-but-implicit ("…after Trump leaves office?"). Ask the
    // model before rejecting so datable questions aren't bounced. setLoading(true)
    // both shows a spinner for the ~1s check AND blocks a double-submit through
    // this window (the `loading` guard above). Kept true on success (the fall-
    // through re-sets it) so there's no off/on flicker.
    setLoading(true);
    const timeframeDone = turn?.phase("timeframe_check");
    const inferable = await hasInferableTimeframe(q, turn?.id ?? null);
    timeframeDone?.();
    if (!inferable) {
      // Don't fail silently: a truly timeframe-less question can't be pinned to a
      // stable resolution date, so we hold off — but we MUST say why, or Enter
      // just looks broken/disabled (bug 07545325 — the reporter hit Enter a dozen
      // times on "will pittsburgh population grow"). Keep their text so they can
      // add a date and re-send.
      setLoading(false);
      setInput(q);
      pushSayerMsgRef?.current?.(
        "I can’t forecast that yet — it needs a timeframe so I can set a resolution date. Add a date or horizon (e.g. “… by 2027?” or “… within the next year?”) and press Enter.",
        { target: replyTarget },
      );
      finish("rejected_no_timeframe");
      return;
    }
    // Inferable → fall through; preprocess resolves the implicit date.
  }
  // Set loading synchronously BEFORE awaiting guardForTos so the existing
  // `if(loading)return` check above blocks a fast double-Enter from
  // slipping through the guard window. Unwind if the guard fails.
  setLoading(true);
  const tosDone = turn?.phase("tos_guard");
  const tosOk = await guardForTos();
  tosDone?.();
  if (!tosOk) {
    setLoading(false);
    finish("tos_blocked");
    return;
  }
  setInput("");
  recordLegacyEvent({
    event: "ui.forecast.submit",
    engine: engineToUse,
    question: legacyText(q),
  });
  recordTextEvent({
    event: "submit.question",
    text: q,
    msg_id: msgId,
    chat_turn_id: turn?.id ?? null,
    question_type: preprocessMode,
    engine: engineToUse,
    compare_engine: compareMode ? (deps.ui.compareEngine ?? null) : null,
  });
  // New submission cycle — reset confirmation flag and arm an abort signal
  // that resetForecast() can fire to short-circuit a CLEAR landing while
  // preprocess is in flight (otherwise the panel + doForecast would run
  // for a forecast the user already cleared).
  preprocessConfirmedRef.current = false;
  const submitAc = new AbortController();
  submitAbortRef.current = submitAc;
  // Tour-prompted first forecast only: clear the contract note flag so it doesn't fire again.
  const contractNote = contractNotePendingRef.current;
  if (contractNote) contractNotePendingRef.current = false;
  // Enter the awaiting phase IMMEDIATELY with a placeholder resolution date — animates spine + "generating forecast" pulse
  // before preprocess returns. The real resolution date overrides this once preprocess completes.
  const placeholderResolutionDate = new Date(Date.now() + 365 * 86400000)
    .toISOString()
    .slice(0, 10);
  // `pendingId` (minted with the intent above) is unique so we can match it in the log later
  // and replace (instead of prepending a duplicate) when the real forecast arrives.
  // Stash the tour's pendingId synchronously so startTour can eager-create the
  // example forecast row right after this submit (store.getState().activeRunId only
  // catches up on the next render, so it can't be read inline after submit()).
  if (getTourFixture()) lastTourPendingIdRef.current = pendingId;
  // A NEW forecast must not inherit the previous one's plotted signals. Left
  // alone, `series` still holds the active forecast's selection when the new run
  // goes active below, and the per-forecast save effect (App.jsx) then stamps it
  // onto the new row — forecast A's signals persisted onto B. #2909 fixed that
  // contamination along the navigation axis and deliberately left this one, on
  // the grounds that the save effect can't infer intent; a submit is explicit, so
  // shouldClearSelectionOnSubmit decides it here (blank-slate picks carry, a rerun
  // of the same question keeps its signals) for the run the question was asked on:
  // `replyTarget`, captured before the timeframe / ToS awaits above (C-10). The live
  // store is asked only whether that run is still the one on screen; a forecast the
  // user opened meanwhile never lends the new run its plotted signals, whatever its
  // question (`submitForecast.interleaving.test.js`).
  // Ordering: after every early-return guard above (a bounced submit must not
  // wipe the panel) and before activeSet, so the save effect never observes the
  // old selection paired with the new run.
  const stateAtSubmit = store.getState();
  const askedRunOnScreen = stateAtSubmit.activeRunId === replyTarget.runId;
  const activeAtSubmit =
    askedRunOnScreen && replyTarget.runId
      ? (stateAtSubmit.runsById?.[replyTarget.runId] ?? null)
      : null;
  if (!askedRunOnScreen || shouldClearSelectionOnSubmit(activeAtSubmit, q)) clearSeriesSelection();
  // Add the pending forecast to the log immediately so the user sees the entry
  // (with no % yet) and the % populates when generation completes.
  // Dual-write run creation to the store (runId === pendingId; root → conversationId === runId).
  // During the onboarding tour the fixture forecast is the only one created here with
  // __tourFixture set — mark it isTourExample so pushSayerMsg persists ITS guide narration
  // (and only its) once it has a real forecastId (eager-created in startTour). Non-tour
  // submissions pass false (the makeRun default).
  // Stamp backend:engineToUse at creation so run.backend is the authoritative
  // orchestrator on EVERY path (compare legs + branches already do this). Without
  // it the single-forecast run starts backend:null and the write-once traceAllocated
  // stamps the x-forecast-backend deploy label — which branch-create then re-sends as
  // `orchestrator`, yielding backend 400 "unknown orchestrator" (the recurring bug).
  dispatchRun(
    runCreated(
      makeRun({
        runId: pendingId,
        conversationId: pendingId,
        question: q,
        backend: engineToUse,
        status: STATUS.PREPROCESSING,
        isTourExample: !!getTourFixture(),
        triggerModel: compareMode ? effectiveTriggerModel(triggerLlm) : null,
      }),
    ),
  );
  // Seed a placeholder contract carrying the placeholder resolution date so the
  // awaiting spine spans today→resolution (and the "Generating Forecast" label
  // centers with dash-gaps around it) BEFORE preprocess returns. Without a
  // contract the run's resolution_date is undefined → resScreenX is null →
  // rxClamped collapses to today, so the spine renders as a stub and the label
  // overlaps it. earlyContract overrides this with the real date once preprocess
  // completes. Matches the placeholder date autoZoom already uses just below.
  // `contractFromPreprocessed` lives in a still-JS module; its inferred return has
  // `probability: null`, which the typed `contractSet` rejects. Cast at the boundary.
  dispatchRun(
    contractSet(pendingId, {
      ...contractFromPreprocessed(null, q, placeholderResolutionDate),
      // Until earlyContract replaces it, the question here is the raw typed text.
      placeholder: true,
    } as any),
  );
  // Adopt the draft introduction into the new run's conversation.
  // Capture the entries BEFORE clearing so onTraceAllocated can persist them.
  const adoptedDraftEntries = store.getState()?.conversationsById?.[DRAFT_CONV_ID]?.entries ?? [];
  if (adoptedDraftEntries.length > 0) {
    draftAdoption = adoptedDraftEntries;
    dispatchRun(draftAdopted(pendingId));
    clearDraft();
  }
  dispatchRun(activeSet(pendingId));
  setForecastingPhase("awaiting");
  turn?.mark("placeholder_shown");
  slideCache.current = {};
  autoZoom(placeholderResolutionDate);
  try {
    // Onboarding tour: skip the /preprocess network call + panel. Build a
    // synthetic preprocessed question from the baked fixture so doForecast →
    // genForecastFromServer (short-circuited) renders the cached forecast,
    // reusing the real placeholder/zoom/animation path set up above.
    if (getTourFixture()) {
      const fc = getTourFixture().base;
      const rd = fc.contract?.resolution_date;
      const pp = {
        question_text: q,
        question_type: "binary",
        title: fc.contract?.title || q,
        scheduled_resolution_time: rd ? rd + "T00:00:00Z" : undefined,
      };
      doForecast(deps, { q, engine: engineToUse, preprocessed: pp, pendingId, msgId });
      turn?.finish("tour");
      return;
    }
    // Preprocess through the typed api seam (P5.11), threading submitAbortRef's signal so CLEAR
    // can cancel it. `preprocess` picks the local/remote endpoint, and throws a descriptive
    // error on a non-ok / null-body response — same defensive posture as genForecastFromServer
    // (bug 8885e4c): a non-JSON preprocess would otherwise enter the UI with an empty object.
    const doPP = () =>
      deps.services.api.preprocess(engineToUse, q, {
        signal: submitAc.signal,
        mode: preprocessMode,
        // Ignored by `preprocess` unless mode is "auto" — asserted there rather than
        // branched on here, so the rule lives with the endpoint choice it constrains.
        variantId: preprocessVariantId || "",
        turnId: turn?.id ?? null,
      });
    recordPreprocessRequest(q);
    let preprocessed: any;
    const preprocessDone = turn?.phase("preprocess");
    try {
      preprocessed = await doPP();
      preprocessDone?.();
      intent.mark("preprocess_ms");
    } catch (ppErr: any) {
      preprocessDone?.();
      if (submitAc.signal.aborted) {
        dispatchRun(runRemoved(pendingId));
        finish("aborted");
        return;
      }
      console.log("Preprocess failed, waking the forecast server via /health ...");
      const retryDone = turn?.phase("preprocess_retry");
      try {
        await deps.services.api.health(engineToUse, { turnId: turn?.id ?? null });
        await new Promise((r) => setTimeout(r, 2000));
        preprocessed = await doPP();
        retryDone?.();
        intent.mark("preprocess_ms");
      } catch (_: any) {
        retryDone?.();
        if (submitAc.signal.aborted) {
          dispatchRun(runRemoved(pendingId));
          finish("aborted");
          return;
        }
        throw ppErr;
      }
    }
    // CLEAR landed during preprocess — bail out before showing the panel or
    // calling doForecast for an already-discarded forecast.
    if (submitAc.signal.aborted) {
      dispatchRun(runRemoved(pendingId));
      finish("aborted");
      return;
    }
    recordPreprocessResult(preprocessed);
    // Reject a past resolution date returned by preprocess — don't forecast a
    // date that's already behind us. Explain in chat (the message goes to the
    // DRAFT conversation because runRemoved deletes a root run's own
    // conversation; the draft is what the chat shows once no forecast is active,
    // so it stays visible) and tear down the pending PREPROCESSING run.
    if (isPastResolution(preprocessed.scheduled_resolution_time, exactDeadlineOf(preprocessed))) {
      dispatchRun(
        entryAppended(
          DRAFT_CONV_ID,
          makeEntry({
            role: "assistant",
            content: pastResolutionMessage(
              preprocessed.scheduled_resolution_time,
              exactDeadlineOf(preprocessed),
            ),
          }),
        ),
      );
      setForecastingPhase(null);
      setLoading(false);
      dispatchRun(activeSet(null));
      dispatchRun(runRemoved(pendingId));
      finish("past_resolution");
      return;
    }
    // Populate contract panel with preprocess data (overrides placeholder).
    // Built via the shared helper so submit, resume, and edit-resubmit produce
    // an identical contract shape — and so the invalid-date guard applies here
    // too (a malformed scheduled_resolution_time falls back to the placeholder
    // instead of throwing a RangeError).
    const earlyContract: any = contractFromPreprocessed(preprocessed, q, placeholderResolutionDate);
    // Keep the same pendingId so the keyed spine / forecast-line / "FORECAST" label
    // elements stay mounted across the placeholder→earlyContract transition. Changing
    // active.id forces React to unmount + remount them, which restarts their CSS
    // animations from the beginning — looked like the spine sweep replayed when the
    // preprocessed-question popup appeared.
    // Only refine the visible active state if the user is still on THIS forecast.
    // If they navigated to another row in the log while preprocess was in flight,
    // leaving prev untouched prevents A's question/contract from bleeding into B.
    // The forecasts[] entry is always patched so a later log-click reattach sees
    // the preprocessed contract instead of the bare placeholder.
    const userOnThisAtPreprocess = store.getState().activeRunId === pendingId;
    // Dual-write preprocess contract patch to the store.
    dispatchRun(contractSet(pendingId, earlyContract));
    if (
      userOnThisAtPreprocess &&
      earlyContract.resolution_date &&
      earlyContract.resolution_date !== placeholderResolutionDate
    )
      autoZoom(earlyContract.resolution_date, false);
    // Show panel AND start forecast in background — panel doesn't block
    // `clientId` (not `forecastId`) — this holds the client-side row id
    // (pendingId), not the server/DB forecast UUID that `forecastId` refers
    // to elsewhere in this file (e.g. `active.forecastId`). The render gate
    // below compares against `active.id`, which IS the client-side row id.
    //
    // EXCEPT when the contract server found readings of the deadline that could change
    // the answer: then the forecast is HELD until the user picks one in the panel
    // (commands/applyResolutionMoment starts it). Running on a guess there would only
    // produce a confident forecast of the wrong question.
    const awaitingChoice = resolutionOptionsOf(preprocessed).length > 0;
    // What SUBMIT EDIT re-routes the edited text with (commands/editResubmit); `forced` is the
    // honoured prefix, which the edit buffer shows so an edit keeps it.
    const routing = {
      multiwayEnabled: !!multiwayEnabled,
      autoQuestionTypeEnabled: !!autoQuestionTypeEnabled,
      variantId: preprocessVariantId || "",
      forced: routed.forced ? preprocessMode : null,
    };
    setPendingPreprocess({
      original: q,
      question: preprocessed,
      engine: engineToUse,
      editing: false,
      editText: editBufferOf(preprocessed.question_text || q, routing),
      clientId: pendingId,
      awaitingChoice,
      msgId,
      routing,
    });
    // The preprocessed contract panel is what the user was waiting for: the turn ends
    // here. The forecast itself runs for minutes and is timed by its own trace. If the
    // user navigated to another forecast while preprocess ran, the panel's render gate
    // hides it, so nothing was shown.
    turn?.finish(
      !userOnThisAtPreprocess
        ? "navigated_away"
        : awaitingChoice
          ? "awaiting_choice"
          : "preprocessed",
    );
    if (awaitingChoice) return;
    doForecast(deps, {
      q: preprocessed.question_text || q,
      engine: engineToUse,
      preprocessed,
      pendingId,
      msgId,
      routing,
    });
  } catch (_e: any) {
    // Preprocess failed — run forecast directly without panel
    turn?.finish("preprocess_failed");
    doForecast(deps, { q, engine: engineToUse, preprocessed: null, pendingId, msgId });
  }
}
